import { pickRandomFromArray } from "~/solidJs/shared/helpers/mathRandom";
import { DebugFlags, isDebugFlag } from "~/solidJs/shared/helpers/testHelpers";

/**
 * modification object, with needed inforamtion
 */
export type Modification = {
	/**
	 * socket name to modify
	 */
	fromSocketName: string;
	/**
	 * variants from pick to
	 */
	value: string[];
};

/**
 * map of other socket modifications
 */
type ModificationsList = {
	[toSocketName: string]: Modification;
};

/**
 * list of socket modidification that will affect other sockets
 */
type SocketModifications = {
	[fromSocketName: string]: ModificationsList;
};

export interface INodeModifier {
	/**
	 * Adds a modification to a specific socket.
	 * @param fromSocketName - The name of the socket to add the modification to.
	 * @param modification - The modification object containing the details to be added.
	 */
	addModification(fromSocketName: string, modification: Modification): void;

	/**
	 * Deletes a specific modification from a specific socket.
	 * @param toSocketName - The name of the socket to delete the modification from.
	 * @param fromSocketName - The name of the socket from which the modification originated.
	 * @returns A boolean indicating whether the deletion was successful.
	 */
	deleteModification(fromSocketName: string, toSocketName: string): boolean;

	/**
	 * Deletes a modification from a specific socket.
	 * @param fromSocketName - The name of the socket from which we need to delete all modifications.
	 * @returns A boolean indicating whether the deletion was successful.
	 */
	deleteAllSocketModifications(fromSocketName: string): boolean;

	/**
	 * Retrieves the modifications for a specific socket.
	 * @param toSocketName - The name of the socket to retrieve the modifications for.
	 * @returns An array of modifications.
	 */
	getSocketModifications(fromSocketName: string): Modification[];

	/**
	 * Retrieves all the modifications in sorted order.
	 * @returns An array of modifications in sorted order.
	 */
	getAllSocketsSortedModifications(): Modification[];
}

export class NodeModifier implements INodeModifier {
	modificationState: SocketModifications = {};

	addModification(fromSocketName: string, modification: Modification): void {
		if (!isSocketModificationExist(fromSocketName, this.modificationState)) {
			this.modificationState[fromSocketName] = {};
		}
		this.modificationState[fromSocketName][modification.fromSocketName] = modification;

		if (isDebugFlag(DebugFlags.NodeModifierDebug)) {
			console.log("[NodeModifier] Added modification:", {
				fromSocketName,
				modification,
				currentState: JSON.parse(JSON.stringify(this.modificationState))
			});
		}
	}

	deleteModification(fromSocketName: string, toSocketName: string): boolean {
		if (!isSocketModificationExist(fromSocketName, this.modificationState)) {
			return false;
		}

		if (!(toSocketName in this.modificationState[fromSocketName])) {
			return false;
		}
		const result = delete this.modificationState[fromSocketName][toSocketName];

		if (isDebugFlag(DebugFlags.NodeModifierDebug)) {
			console.log("[NodeModifier] Deleted modification:", {
				fromSocketName,
				toSocketName,
				result,
				currentState: JSON.parse(JSON.stringify(this.modificationState))
			});
		}

		return result;
	}

	deleteAllSocketModifications(fromSocketName: string): boolean {
		if (!isSocketModificationExist(fromSocketName, this.modificationState)) {
			return false;
		}
		const result = delete this.modificationState[fromSocketName];

		if (isDebugFlag(DebugFlags.NodeModifierDebug)) {
			console.log("[NodeModifier] Deleted all socket modifications:", {
				fromSocketName,
				result,
				currentState: JSON.parse(JSON.stringify(this.modificationState))
			});
		}

		return result;
	}

	getSocketModifications(fromSocketName: string): Modification[] {
		const socketModification = this.modificationState[fromSocketName];
		if (!socketModification) {
			return [];
		}
		const out = Object.values(socketModification);
		out.sort((a, b) => a.fromSocketName.localeCompare(b.fromSocketName));
		return out;
	}

	getAllSocketsSortedModifications(): Modification[] {
		const out: Modification[] = [];
		const modificationEntries = Object.entries(this.modificationState);
		modificationEntries.sort((a, b) => a[0].localeCompare(b[0]));
		for (const toModify of modificationEntries) {
			for (const modification of Object.values(toModify[1])) {
				out.push(modification);
			}
		}
		return out;
	}
}

function isSocketModificationExist(socketName: string, modifications: SocketModifications) {
	return socketName in modifications && modifications[socketName];
}

export function applyModificationListToString(
	value: string,
	modificationList: Modification[]
): string {
	let result = value;
	for (const modification of modificationList) {
		const stringModification = pickRandomFromArray(modification.value);
		result = stringModification.replace("_____", result);
	}
	return result;
}

export function applyModificationToString(value: string, modification: Modification): string {
	const stringModification = pickRandomFromArray(modification.value);
	const result = stringModification.replace("_____", value);
	return result;
}
